"use client"

// =====================================================
// BOOTH — Meta Pixel loader + SPA pageview tracking
// =====================================================
// Two pieces, mirroring how the PostHog integration is split:
//
//   1. MetaPixel         — injects the standard Meta base snippet once,
//                          which calls fbq('init', <id>) and fires the
//                          first PageView.
//   2. MetaPixelPageView — fires PageView on subsequent App Router
//                          navigations. Next's client router doesn't
//                          reload the page, so without this the pixel
//                          only ever sees the entry URL.
//
// Both render nothing and both no-op when NEXT_PUBLIC_META_PIXEL_ID is
// unset, so local dev and preview builds stay clean of ad tracking.
//
// The OAuth signup conversion used to live here. It now fires Meta AND
// TikTok from components/analytics/signup-conversion.tsx — both pixels
// read the same one-shot ?booth_signup flag, so they have to share a
// single reader.
// =====================================================

import Script from "next/script"
import { Suspense, useEffect, useRef } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { META_PIXEL_ID, trackMeta, trackMetaPageView } from "@/lib/meta-pixel"

export function MetaPixel() {
  if (!META_PIXEL_ID) return null

  return (
    <>
      <Script
        id="meta-pixel"
        strategy="afterInteractive"
        // Meta's official base code, verbatim apart from the interpolated
        // pixel id. It self-guards against double-injection via `f.fbq`.
        dangerouslySetInnerHTML={{
          __html: `
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${META_PIXEL_ID}');
fbq('track', 'PageView');
          `,
        }}
      />
      {/* Fallback for JS-disabled visitors so the dataset still records a
          PageView. Meta's setup checker also looks for this tag. */}
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`}
        />
      </noscript>
    </>
  )
}

function MetaPixelPageViewInner() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  // The base snippet already fired PageView for the entry URL. Skipping the
  // first effect run keeps the landing hit from being counted twice, which
  // would otherwise inflate every ad's reported reach.
  const skippedInitial = useRef(false)

  useEffect(() => {
    if (!META_PIXEL_ID || !pathname) return
    if (!skippedInitial.current) {
      skippedInitial.current = true
      return
    }
    trackMetaPageView()
  }, [pathname, searchParams])

  return null
}

// useSearchParams() opts the subtree into client rendering, so it needs a
// Suspense boundary to avoid de-opting the whole route (same as PostHogPageView).
export function MetaPixelPageView() {
  return (
    <Suspense fallback={null}>
      <MetaPixelPageViewInner />
    </Suspense>
  )
}
